// =============================================================================
// runVarietyMix.ts — a run's DECLARED variety versus what was actually tipped.
// Plan: docs/plans/BERRY-RUN-VARIETY-PLAN-2026-08-04.md (Stage 0).
//
// A run names one variety and ~94% of pallets inherit it. Berry crates each
// hold ONE variety but at peak they run back-to-back, so a run can receive
// fruit it does not describe. Nothing is lost when that happens — every tip
// still knows its own variety — so the mix is derivable, and this module is
// the one place that decides what "mixed" means.
//
// Reads the `run_variety_mix` view (mig 20260804150000). Pure: no I/O.
// =============================================================================

/** One row of the run_variety_mix view. */
export interface RunVarietyRow {
  variety:    string | null
  tips:       number
  containers: number
  net_kg:     number | string
}

export interface VarietyShare {
  variety:  string
  tips:     number
  containers: number
  netKg:    number
  /** 0–1 of the run's tipped kg. Falls back to tip count if kg is absent. */
  share:    number
  /** True when this is not the variety the run declares. */
  unexpected: boolean
}

/**
 * `clean`     — everything tipped matches the declared variety.
 * `mixed`     — more than one variety went into the run.
 * `mislabelled` — a single variety was tipped, but not the declared one.
 * `unknown`   — nothing tipped yet, or no variety resolved on any tip.
 */
export type RunMixState = 'clean' | 'mixed' | 'mislabelled' | 'unknown'

export interface RunMix {
  state:    RunMixState
  declared: string | null
  shares:   VarietyShare[]
  totalKg:  number
  /** kg tipped that is NOT the declared variety — the size of the problem. */
  unexpectedKg: number
}

const num = (v: number | string | null | undefined): number => {
  const n = typeof v === 'string' ? Number(v) : v
  return Number.isFinite(n) ? (n as number) : 0
}

/** Case/space-insensitive compare — 'Snowchaser' and 'snowchaser ' are one. */
const sameVariety = (a: string | null, b: string | null): boolean =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase()

export function buildRunMix(declared: string | null, rows: RunVarietyRow[]): RunMix {
  const withVariety = rows.filter((r) => (r.variety ?? '').trim() !== '')

  // Fold rows whose variety differs only by case/spacing into one share. The
  // two source lanes (cooling-batch vs delivery) each store free `TEXT`, so the
  // same variety can arrive as 'Snowchaser' on one and 'snowchaser ' on another
  // feeding one run; the view groups on the raw string and emits a row per
  // distinct spelling. Without this merge, one variety looks like two →
  // `shares.length > 1` → a clean single-variety run is falsely flagged 'mixed'
  // (and one tipped in two casings, one matching declared, would read
  // 'mixed' instead of 'clean'). Uses the same `sameVariety` normaliser this
  // module already applies to the `unexpected` flag; keeps the first-seen
  // display casing.
  const mergedByKey = new Map<string, RunVarietyRow>()
  for (const r of withVariety) {
    const key = (r.variety as string).trim().toLowerCase()
    const prev = mergedByKey.get(key)
    if (prev) {
      prev.tips = num(prev.tips) + num(r.tips)
      prev.containers = num(prev.containers) + num(r.containers)
      prev.net_kg = num(prev.net_kg) + num(r.net_kg)
    } else {
      mergedByKey.set(key, {
        variety:    (r.variety as string).trim(),
        tips:       num(r.tips),
        containers: num(r.containers),
        net_kg:     num(r.net_kg),
      })
    }
  }
  const known = [...mergedByKey.values()]

  const totalKg = known.reduce((s, r) => s + num(r.net_kg), 0)
  const totalTips = known.reduce((s, r) => s + num(r.tips), 0)

  // Share by kg where we have weights, else by tip count — a run tipped by
  // crate count with no weights must still show proportions rather than 0%.
  const denom = totalKg > 0 ? totalKg : totalTips
  const basis = (r: RunVarietyRow) => (totalKg > 0 ? num(r.net_kg) : num(r.tips))

  const shares: VarietyShare[] = known
    .map((r) => ({
      variety:    (r.variety as string).trim(),
      tips:       num(r.tips),
      containers: num(r.containers),
      netKg:      num(r.net_kg),
      share:      denom > 0 ? basis(r) / denom : 0,
      unexpected: !sameVariety(r.variety, declared),
    }))
    .sort((a, b) => b.share - a.share || a.variety.localeCompare(b.variety))

  const unexpectedKg = shares.filter((s) => s.unexpected).reduce((s, r) => s + r.netKg, 0)

  let state: RunMixState = 'unknown'
  if (shares.length > 1) state = 'mixed'
  else if (shares.length === 1) state = shares[0].unexpected ? 'mislabelled' : 'clean'

  return { state, declared: declared ?? null, shares, totalKg, unexpectedKg }
}

/** Bucket a bulk `run_variety_mix` read by run, for a many-runs view. */
export function groupMixByRun<T extends { run_id: string }>(rows: T[]): Map<string, T[]> {
  const by = new Map<string, T[]>()
  for (const r of rows) {
    const list = by.get(r.run_id)
    if (list) list.push(r)
    else by.set(r.run_id, [r])
  }
  return by
}

export const isMixNoteworthy = (s: RunMixState): boolean =>
  s === 'mixed' || s === 'mislabelled'

/**
 * The runs in `runs` that received fruit they do not declare, each with its
 * mix. Pure so the "how big is this?" answer is testable without a component.
 */
export function mismatchedRuns<R extends { run_id: string; variety: string | null }>(
  runs: R[],
  rows: Array<RunVarietyRow & { run_id: string }>,
): Array<{ run: R; mix: RunMix }> {
  const byRun = groupMixByRun(rows)
  return runs
    .map((run) => ({ run, mix: buildRunMix(run.variety, byRun.get(run.run_id) ?? []) }))
    .filter((x) => isMixNoteworthy(x.mix.state))
}

export const MIX_STATE_LABEL: Record<RunMixState, string> = {
  clean:       'matches',
  mixed:       'mixed varieties',
  mislabelled: 'wrong variety',
  unknown:     'nothing tipped',
}

/** Only the two states worth a manager's attention get a colour. */
export const MIX_STATE_CHIP: Record<RunMixState, string | null> = {
  clean:       null,
  mixed:       'bg-amber-900/60 text-amber-300',
  mislabelled: 'bg-red-900/60 text-red-300',
  unknown:     null,
}

export function formatShare(v: number): string {
  if (!(v > 0)) return '0%'
  const pct = v * 100
  // Never round a real minority variety away to "0%" — that is exactly the
  // fruit somebody needs to see.
  return pct < 1 ? '<1%' : `${Math.round(pct)}%`
}
